import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useContext,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Draggable from "react-draggable";

import { useUniqueId } from "../hooks/useUniqueId";

import styles from "./ToggleButton.scss";
import A11yText from "../A11yText";
import { ThemeContext } from "../Theme";

export const ToggleButton = forwardRef((props, forwardedRef) => {
  const {
    checked: controlledChecked,
    onToggle,
    children,
    offText,
    onText,
    disabled,
    customClass,
    buttonCustomClass,
    ...restProps
  } = props;
  const { id } = restProps;
  const toggleButtonId =
    id || `ToggleButton-${useUniqueId("ToggleButton", props) + 1}`;

  const [lastControlledChecked, setLastControlledChecked] = useState(
    controlledChecked
  );
  const [checked, setChecked] = useState(controlledChecked);
  if (controlledChecked !== lastControlledChecked) {
    setLastControlledChecked(controlledChecked);
    setChecked(controlledChecked);
  }
  const [focusButton, setFocusButton] = useState(true);

  const context = useContext(ThemeContext);

  const ref = useRef();
  const circleRef = useRef();

  function onToggleClick() {
    setChecked(!checked);
    onToggle(!checked);
  }

  function hideFocus() {
    setFocusButton(false);
  }

  function showFocus() {
    setFocusButton(true);
  }

  function handleDragStop(e, data) {
    e.stopPropagation();
    const isOn = checked;
    if (isOn) {
      if (data.x < 14) {
        setChecked(false);
        onToggle(false);
      }
    } else if (data.x > 4) {
      setChecked(true);
      onToggle(true);
    }

    if (e.type !== "mouseup") {
      setChecked(!checked);
      onToggle(!checked);
    }
  }

  useEffect(() => {
    const component = ref.current;

    component.addEventListener("mousedown", hideFocus);
    component.addEventListener("keydown", showFocus);
  }, []);

  return (
    <div
      ref={forwardedRef}
      className={cx(styles.wrapper, customClass, {
        "is-disabled": disabled,
        [styles[context]]: context,
      })}
    >
      <button
        type="button"
        role="switch"
        disabled={disabled}
        aria-checked={checked}
        id={toggleButtonId}
        onClick={onToggleClick}
        className={cx(styles.switch, buttonCustomClass, {
          [styles.switch_witchout_focus]: !focusButton,
        })}
        aria-invalid={!checked}
        ref={ref}
      >
        <Draggable
          axis="x"
          position={{ x: checked ? 18 : 0, y: 0 }}
          bounds={{ left: 0, right: 18 }}
          nodeRef={circleRef}
          scale={1}
          onStop={handleDragStop}
        >
          <span
            className={cx(styles.circleWrapper, {
              [styles.circleWrapperActive]: checked,
            })}
            ref={circleRef}
          >
            <span
              disabled={disabled}
              className={cx(styles.circle, {
                [styles.circleActive]: checked,
              })}
            >
              <A11yText>{`${checked ? `${onText}` : `${offText}`}`}</A11yText>
            </span>
          </span>
        </Draggable>
      </button>
      <label htmlFor={toggleButtonId} className={styles.label}>
        {children}
      </label>
    </div>
  );
});

ToggleButton.propTypes = {
  onText: PropTypes.string,
  offText: PropTypes.string,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  onToggle: PropTypes.func,
  customClass: PropTypes.string,
  buttonCustomClass: PropTypes.string,
};

ToggleButton.defaultProps = {
  onText: "",
  offText: "",
  checked: false,
  disabled: false,
  onToggle: () => null,
  customClass: "",
  buttonCustomClass: "",
};

ToggleButton.Variant = {
  themeDark: "togglebutton--theme_dark",
};

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
